import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';
import {
  _Array,
  _Boolean,
  _Number,
  _Object,
  _String,
} from './forms-ai-v1-interactive-form-session-interactive-form-sessions.types';

function resolveWixFormsAiV1InteractiveFormSessionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    '*.wixforms.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/forms/ai/v1/chat-settings',
        destPath: '/v1/chat-settings',
      },
      {
        srcPath: '/forms/ai/v1/interactive-form-sessions',
        destPath: '/v1/interactive-form-sessions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    'apps.wixel.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
    '*.wixel.com': [
      {
        srcPath: '/_api/form-ai-assistant',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_forms_interactive-form-sessions';

/**
 * Creates an interactive form session for AI-powered conversational form completion.
 *
 * For implementations that require real-time streaming, call [Create Interactive Form Session Streamed](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/create-interactive-form-session-streamed) instead.
 */
export function createInteractiveFormSession(
  payload: object
): RequestOptionsFactory<any> {
  function __createInteractiveFormSession({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'form.createdDate' },
          { path: 'form.updatedDate' },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
          },
          { path: 'form.limitationRule.dateTimeDeadline' },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
          },
          { path: 'form.disabledFormMessage.metadata.createdTimestamp' },
          { path: 'form.disabledFormMessage.metadata.updatedTimestamp' },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
          },
          { path: 'form.quizSettings.message.metadata.createdTimestamp' },
          { path: 'form.quizSettings.message.metadata.updatedTimestamp' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'form.fields.validation.number.maximum' },
          { path: 'form.fields.validation.number.minimum' },
          { path: 'form.fields.validation.number.multipleOf' },
          { path: 'form.fields.validation.number.enum', isRepeated: true },
          { path: 'form.fields.validation.array.items.number.maximum' },
          { path: 'form.fields.validation.array.items.number.minimum' },
          { path: 'form.fields.validation.array.items.number.multipleOf' },
          {
            path: 'form.fields.validation.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.maximum',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.minimum',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.enum',
            isRepeated: true,
          },
          { path: 'form.fields.validation.object.properties.*.number.maximum' },
          { path: 'form.fields.validation.object.properties.*.number.minimum' },
          {
            path: 'form.fields.validation.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.fields.validation.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.maximum',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.minimum',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.multipleOf',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.default',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.maximum',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.minimum',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          { path: 'form.deletedFields.validation.number.maximum' },
          { path: 'form.deletedFields.validation.number.minimum' },
          { path: 'form.deletedFields.validation.number.multipleOf' },
          {
            path: 'form.deletedFields.validation.number.enum',
            isRepeated: true,
          },
          { path: 'form.deletedFields.validation.array.items.number.maximum' },
          { path: 'form.deletedFields.validation.array.items.number.minimum' },
          {
            path: 'form.deletedFields.validation.array.items.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.options.item.ratio',
          },
          { path: 'form.disabledFormMessage.nodes.imageData.image.duration' },
          { path: 'form.disabledFormMessage.nodes.mapData.mapSettings.lat' },
          { path: 'form.disabledFormMessage.nodes.mapData.mapSettings.lng' },
          {
            path: 'form.disabledFormMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.appEmbedData.image.duration',
          },
          { path: 'form.disabledFormMessage.nodes.videoData.video.duration' },
          {
            path: 'form.disabledFormMessage.nodes.videoData.thumbnail.duration',
          },
          { path: 'form.disabledFormMessage.nodes.audioData.audio.duration' },
          {
            path: 'form.disabledFormMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'form.disabledFormMessage.nodes.shapeData.shape.duration' },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'form.disabledFormMessage.nodes.tocData.fontSize' },
          { path: 'form.disabledFormMessage.nodes.tocData.itemSpacing' },
          {
            path: 'form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.options.item.ratio',
          },
          { path: 'form.quizSettings.message.nodes.imageData.image.duration' },
          { path: 'form.quizSettings.message.nodes.mapData.mapSettings.lat' },
          { path: 'form.quizSettings.message.nodes.mapData.mapSettings.lng' },
          {
            path: 'form.quizSettings.message.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.appEmbedData.image.duration',
          },
          { path: 'form.quizSettings.message.nodes.videoData.video.duration' },
          {
            path: 'form.quizSettings.message.nodes.videoData.thumbnail.duration',
          },
          { path: 'form.quizSettings.message.nodes.audioData.audio.duration' },
          {
            path: 'form.quizSettings.message.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'form.quizSettings.message.nodes.shapeData.shape.duration' },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'form.quizSettings.message.nodes.tocData.fontSize' },
          { path: 'form.quizSettings.message.nodes.tocData.itemSpacing' },
          {
            path: 'form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
          },
          { path: 'form.quizSettings.passingScorePoints' },
          { path: 'form.quizSettings.passingScorePercentage' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.interactive_form_session',
      method: 'POST' as any,
      methodFqn:
        'wix.forms.ai.v1.InteractiveFormSessionsService.CreateInteractiveFormSession',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1InteractiveFormSessionsServiceUrl({
        protoPath: '/v1/interactive-form-sessions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'interactiveFormSession.form.createdDate' },
              { path: 'interactiveFormSession.form.updatedDate' },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.limitationRule.dateTimeDeadline',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.updatedTimestamp',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'interactiveFormSession.form.fields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
              },
              { path: 'responseChunks.numberInputDetails.multipleOf' },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.maxInclusiveValue',
              },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.maxExclusiveValue',
              },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.minInclusiveValue',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePoints',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePercentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createInteractiveFormSession;
}

/**
 * Creates an interactive form session for AI-powered conversational form completion, with real-time streaming.
 *
 * For implementations that prefer to wait for the complete response, call [Create Interactive Form Session](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/create-interactive-form-session) instead.
 */
export function createInteractiveFormSessionStreamed(
  payload: object
): RequestOptionsFactory<any> {
  function __createInteractiveFormSessionStreamed({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.interactive_form_session',
      method: 'POST' as any,
      methodFqn:
        'wix.forms.ai.v1.InteractiveFormSessionsService.CreateInteractiveFormSessionStreamed',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1InteractiveFormSessionsServiceUrl({
        protoPath: '/v1/interactive-form-sessions/create-streamed',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'responseChunk.numberInputDetails.multipleOf' },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.maxInclusiveValue',
              },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.maxExclusiveValue',
              },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.minInclusiveValue',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePoints',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePercentage',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'interactiveFormSession.form.createdDate' },
              { path: 'interactiveFormSession.form.updatedDate' },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.limitationRule.dateTimeDeadline',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.updatedTimestamp',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createInteractiveFormSessionStreamed;
}

/**
 * Submits a user message to an existing interactive form session and processes the conversational input.
 * User messages support up to 10,000 characters.
 *
 * For implementations that require real-time streaming, call [Send User Message Streamed](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/send-user-message-streamed) instead.
 */
export function sendUserMessage(payload: object): RequestOptionsFactory<any> {
  function __sendUserMessage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.interactive_form_session',
      method: 'POST' as any,
      methodFqn:
        'wix.forms.ai.v1.InteractiveFormSessionsService.SendUserMessage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1InteractiveFormSessionsServiceUrl({
        protoPath:
          '/v1/interactive-form-sessions/{interactiveFormSessionId}/send-user-message',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'interactiveFormSession.form.createdDate' },
              { path: 'interactiveFormSession.form.updatedDate' },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.limitationRule.dateTimeDeadline',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.updatedTimestamp',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'interactiveFormSession.form.fields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
              },
              { path: 'responseChunks.numberInputDetails.multipleOf' },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.maxInclusiveValue',
              },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.maxExclusiveValue',
              },
              {
                path: 'responseChunks.numberInputDetails.rangeLimit.minInclusiveValue',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePoints',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePercentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __sendUserMessage;
}

/**
 * Submits a user message to an existing interactive form session and processes the conversational input, with real-time streaming.
 *
 * For implementations that prefer to wait for the complete response, call [Send User Message](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/send-user-message) instead.
 */
export function sendUserMessageStreamed(
  payload: object
): RequestOptionsFactory<any> {
  function __sendUserMessageStreamed({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.interactive_form_session',
      method: 'POST' as any,
      methodFqn:
        'wix.forms.ai.v1.InteractiveFormSessionsService.SendUserMessageStreamed',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1InteractiveFormSessionsServiceUrl({
        protoPath:
          '/v1/interactive-form-sessions/{interactiveFormSessionId}/send-user-message-streamed',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'responseChunk.numberInputDetails.multipleOf' },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.maxInclusiveValue',
              },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.maxExclusiveValue',
              },
              {
                path: 'responseChunk.numberInputDetails.rangeLimit.minInclusiveValue',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.fields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.array.items.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFields.validation.object.properties.*.array.items.number.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.validation.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.imageData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.appEmbedData.image.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.video.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.audio.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.audioData.coverImage.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.shapeData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.fontSize',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.tocData.itemSpacing',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
                isRepeated: true,
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePoints',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingScorePercentage',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'interactiveFormSession.form.createdDate' },
              { path: 'interactiveFormSession.form.updatedDate' },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.limitationRule.dateTimeDeadline',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.disabledFormMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.createdTimestamp',
              },
              {
                path: 'interactiveFormSession.form.quizSettings.message.metadata.updatedTimestamp',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __sendUserMessageStreamed;
}

/**
 * Generates an AI summary of a form's structure and purpose in natural language, based on the form's fields and configuration, of max 255 characters.
 *
 * The summary describes what the form collects and its intended use case,
 * useful for accessibility, previews, and AI-powered form discovery.
 */
export function generateFormSummary(
  payload: object
): RequestOptionsFactory<any> {
  function __generateFormSummary({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'form.createdDate' },
          { path: 'form.updatedDate' },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image.urlExpirationDate',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
          },
          { path: 'form.limitationRule.dateTimeDeadline' },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
          },
          { path: 'form.disabledFormMessage.metadata.createdTimestamp' },
          { path: 'form.disabledFormMessage.metadata.updatedTimestamp' },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
          },
          { path: 'form.quizSettings.message.metadata.createdTimestamp' },
          { path: 'form.quizSettings.message.metadata.updatedTimestamp' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'form.fields.validation.number.maximum' },
          { path: 'form.fields.validation.number.minimum' },
          { path: 'form.fields.validation.number.multipleOf' },
          { path: 'form.fields.validation.number.enum', isRepeated: true },
          { path: 'form.fields.validation.array.items.number.maximum' },
          { path: 'form.fields.validation.array.items.number.minimum' },
          { path: 'form.fields.validation.array.items.number.multipleOf' },
          {
            path: 'form.fields.validation.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.maximum',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.minimum',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.fields.validation.array.items.object.properties.*.number.enum',
            isRepeated: true,
          },
          { path: 'form.fields.validation.object.properties.*.number.maximum' },
          { path: 'form.fields.validation.object.properties.*.number.minimum' },
          {
            path: 'form.fields.validation.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.fields.validation.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.maximum',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.minimum',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.multipleOf',
          },
          {
            path: 'form.fields.validation.object.properties.*.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.default',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.maximum',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.minimum',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.validation.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
          },
          {
            path: 'form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          { path: 'form.deletedFields.validation.number.maximum' },
          { path: 'form.deletedFields.validation.number.minimum' },
          { path: 'form.deletedFields.validation.number.multipleOf' },
          {
            path: 'form.deletedFields.validation.number.enum',
            isRepeated: true,
          },
          { path: 'form.deletedFields.validation.array.items.number.maximum' },
          { path: 'form.deletedFields.validation.array.items.number.minimum' },
          {
            path: 'form.deletedFields.validation.array.items.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.array.items.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.maximum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.minimum',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
          },
          {
            path: 'form.deletedFields.validation.object.properties.*.array.items.number.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.validation.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.galleryData.options.item.ratio',
          },
          { path: 'form.disabledFormMessage.nodes.imageData.image.duration' },
          { path: 'form.disabledFormMessage.nodes.mapData.mapSettings.lat' },
          { path: 'form.disabledFormMessage.nodes.mapData.mapSettings.lng' },
          {
            path: 'form.disabledFormMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.appEmbedData.image.duration',
          },
          { path: 'form.disabledFormMessage.nodes.videoData.video.duration' },
          {
            path: 'form.disabledFormMessage.nodes.videoData.thumbnail.duration',
          },
          { path: 'form.disabledFormMessage.nodes.audioData.audio.duration' },
          {
            path: 'form.disabledFormMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'form.disabledFormMessage.nodes.shapeData.shape.duration' },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'form.disabledFormMessage.nodes.tocData.fontSize' },
          { path: 'form.disabledFormMessage.nodes.tocData.itemSpacing' },
          {
            path: 'form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
          },
          {
            path: 'form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.galleryData.options.item.ratio',
          },
          { path: 'form.quizSettings.message.nodes.imageData.image.duration' },
          { path: 'form.quizSettings.message.nodes.mapData.mapSettings.lat' },
          { path: 'form.quizSettings.message.nodes.mapData.mapSettings.lng' },
          {
            path: 'form.quizSettings.message.nodes.pollData.poll.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.appEmbedData.image.duration',
          },
          { path: 'form.quizSettings.message.nodes.videoData.video.duration' },
          {
            path: 'form.quizSettings.message.nodes.videoData.thumbnail.duration',
          },
          { path: 'form.quizSettings.message.nodes.audioData.audio.duration' },
          {
            path: 'form.quizSettings.message.nodes.audioData.coverImage.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'form.quizSettings.message.nodes.shapeData.shape.duration' },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'form.quizSettings.message.nodes.tocData.fontSize' },
          { path: 'form.quizSettings.message.nodes.tocData.itemSpacing' },
          {
            path: 'form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
          },
          {
            path: 'form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
            isRepeated: true,
          },
          {
            path: 'form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
          },
          {
            path: 'form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
            isRepeated: true,
          },
          {
            path: 'form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
          },
          { path: 'form.quizSettings.passingScorePoints' },
          { path: 'form.quizSettings.passingScorePercentage' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.forms.ai.v1.interactive_form_session',
      method: 'POST' as any,
      methodFqn:
        'wix.forms.ai.v1.InteractiveFormSessionsService.GenerateFormSummary',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixFormsAiV1InteractiveFormSessionsServiceUrl({
        protoPath: '/v1/interactive-form-sessions/generate-form-summary',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __generateFormSummary;
}
